import { decodePixels, hashSeed, Rng, type Picture } from "@enjoyment/core";
import { CLASSIC_PICTURES } from "./classicPack";
import { SPRITES, type PixelSpriteDef } from "./sprites";

/**
 * Procedural 16x16 pixel "creature" sprites (mirrored noise + palette), used as the reveal image
 * until tools/art-factory supplies real pixel art. Deterministic per imageId so every player sees
 * the same picture for the same level (shareable, comparable).
 */
export interface PixelImage {
  size: number;
  /** row-major palette indices; -1 = transparent */
  pixels: number[];
  palette: string[];
  /** Display names (Turkish, English) for the win card and share text; procedural images have none. */
  name?: string;
  nameEn?: string;
  /** Pack picture id when the image comes from pictures/pack-1.json (has an HD twin). */
  sourceId?: string;
}

function fromSprite(def: PixelSpriteDef): PixelImage {
  const palette = Object.values(def.key);
  const index = new Map(Object.keys(def.key).map((ch, i) => [ch, i]));
  const size = 16;
  const pixels: number[] = [];
  for (let y = 0; y < size; y++) {
    const row = def.rows[y] ?? "";
    for (let x = 0; x < size; x++) {
      const ch = row[x] ?? ".";
      pixels.push(index.get(ch) ?? -1);
    }
  }
  return { size, pixels, palette, name: def.name, nameEn: def.nameEn };
}

/**
 * Resolve a level's imageId to a picture: hand-made sprites cycle first ("img-N"), anything
 * unknown falls back to a procedural creature so a level never lacks a reveal.
 */
export function resolveImage(imageId: string): PixelImage {
  const m = /^img-(\d+)$/.exec(imageId);
  if (m && SPRITES.length) {
    const n = Number(m[1]);
    if (n >= 1 && n <= SPRITES.length) return fromSprite(SPRITES[n - 1]!);
    if (n > SPRITES.length && CLASSIC_PICTURES.length) {
      const source = CLASSIC_PICTURES[(n - SPRITES.length - 1) % CLASSIC_PICTURES.length]!;
      const picture: Picture = {
        id: source.id,
        theme: "classic",
        size: 16,
        palette: [...source.palette],
        pixels: source.pixels,
        label: { tr: source.name, en: source.nameEn },
      };
      return { size: picture.size, pixels: decodePixels(picture), palette: picture.palette, name: source.name, nameEn: source.nameEn, sourceId: source.id };
    }
  }
  const bySpriteId = SPRITES.find((d) => d.id === imageId);
  if (bySpriteId) return fromSprite(bySpriteId);
  return generatePixelImage(imageId);
}

const PALETTES: string[][] = [
  ["#2b2d42", "#ef233c", "#ffd166", "#06d6a0", "#118ab2", "#ffffff"],
  ["#22223b", "#f8961e", "#f9c74f", "#90be6d", "#43aa8b", "#f94144"],
  ["#1d3557", "#e63946", "#f1faee", "#a8dadc", "#457b9d", "#ffb703"],
  ["#3a0ca3", "#f72585", "#7209b7", "#4cc9f0", "#4361ee", "#ffffff"],
  ["#264653", "#2a9d8f", "#e9c46a", "#f4a261", "#e76f51", "#ffffff"],
];

export function generatePixelImage(imageId: string, size = 16): PixelImage {
  const rng = new Rng(hashSeed(imageId));
  const palette = PALETTES[rng.int(PALETTES.length)]!;
  const half = Math.ceil(size / 2);
  const pixels = new Array<number>(size * size).fill(-1);
  // Body mask: cellular-ish noise on the left half, mirrored.
  const mask: boolean[] = [];
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < half; x++) {
      const cx = x / half; // 0 at edge, 1 at center
      const cy = Math.abs(y - size / 2) / (size / 2); // 0 center, 1 edge
      const p = 0.15 + cx * 0.6 - cy * 0.35;
      mask.push(rng.next() < p);
    }
  }
  // Smooth once to avoid speckle.
  const at = (x: number, y: number) => (x < 0 || y < 0 || x >= half || y >= size ? false : mask[y * half + x]!);
  const smooth: boolean[] = [];
  for (let y = 0; y < size; y++)
    for (let x = 0; x < half; x++) {
      let n = 0;
      for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) if (at(x + dx, y + dy)) n++;
      smooth.push(n >= 5 || (at(x, y) && n >= 3));
    }
  const bodyColor = 1 + rng.int(palette.length - 2);
  const accentColor = 1 + ((bodyColor + 1 + rng.int(palette.length - 3)) % (palette.length - 1));
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < half; x++) {
      if (!smooth[y * half + x]) continue;
      // outline where neighbor is empty
      const edge =
        !smooth[y * half + Math.max(0, x - 1)] ||
        !(y > 0 && smooth[(y - 1) * half + x]) ||
        !(y < size - 1 && smooth[(y + 1) * half + x]);
      let col = edge ? 0 : bodyColor;
      if (!edge && rng.next() < 0.12) col = accentColor;
      pixels[y * size + x] = col;
      pixels[y * size + (size - 1 - x)] = col;
    }
  }
  // Eyes: two white pixels symmetric near upper-middle.
  const ey = Math.floor(size * 0.4);
  const ex = Math.floor(half * 0.55);
  if (pixels[ey * size + ex] !== -1) {
    pixels[ey * size + ex] = palette.length - 1;
    pixels[ey * size + (size - 1 - ex)] = palette.length - 1;
  }
  return { size, pixels, palette };
}

/** Deterministic reveal order: spiral from center outward, so reveal reads as "growing". */
export function revealOrder(size: number): number[] {
  const idx: { i: number; d: number }[] = [];
  const c = (size - 1) / 2;
  for (let y = 0; y < size; y++)
    for (let x = 0; x < size; x++) idx.push({ i: y * size + x, d: Math.hypot(x - c, y - c) + ((x * 7 + y * 13) % 5) * 0.05 });
  idx.sort((a, b) => a.d - b.d);
  return idx.map((v) => v.i);
}
